import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from '../apolloClient';
import { gql } from '@apollo/client';
import type { ICurrentUser } from '../interfaces/ICurrentUser';
import { type IPipeline } from './deploymentsSlice';
import { type IVerticalNavLinkTab } from '../components/nav/VerticalNav';

interface IAccountState {
  currentUser?: ICurrentUser;
  broadcastIdsAcknowledged: number[];
  tabs: IVerticalNavLinkTab[];
  requestStatus?: 'idle' | 'loading' | 'failed';
  docsPipelines?: IPipeline[];
  tokenRefreshRequired: boolean;
  tokenRefreshAuto: boolean;
}

const initialState: IAccountState = {
  broadcastIdsAcknowledged: [],
  tabs: [],
  tokenRefreshAuto: true,
  tokenRefreshRequired: false,
};

export const fetchDocsPipelines = createAsyncThunk('docs/fetch', async (token: string) => {
  const GET_DOCS_PIPELINES = gql`
    query getDocsPipelines {
      project(fullPath: "snowflake/shared/documentation") {
        pipelines(ref: "main") {
          nodes {
            id
            path
            jobs {
              nodes {
                id
                name
                status
              }
            }
          }
        }
      }
    }
  `;
  let response: any = { data: { pipelines: [] } };
  try {
    response = await client.query({
      query: GET_DOCS_PIPELINES,
      context: { headers: { authorization: `Bearer ${token}`, 'DataOps-Operation-Name': 'getDocsPipelines' } },
    });
  } catch (error) {
    console.error('docs error', error);
  }
  return response.data.project?.pipelines.nodes.map((pipeline: any) => {
    return {
      id: pipeline.id,
      path: pipeline.path,
      jobs: pipeline.jobs.nodes.map((job: any) => {
        return {
          id: job.id,
          name: job.name,
          status: job.status,
        };
      }),
    };
  });
});

export const fetchUserStars = createAsyncThunk('user/fetch/stars', async (token: string) => {
  const GET_USER_STARS = gql`
    query getUserStars {
      currentUser {
        starredProjects {
          nodes {
            id
          }
        }
      }
    }
  `;
  const response = await client.query({
    query: GET_USER_STARS,
    context: { headers: { authorization: `Bearer ${token}`, 'DataOps-Operation-Name': 'getUserStars' } },
  });
  return response.data.currentUser;
});

export const fetchUser = createAsyncThunk('user/fetch', async (token: string) => {
  const GET_USER = gql`
    query getUser {
      currentUser {
        id
        username
        avatarUrl
        emails {
          nodes {
            email
          }
        }
        starredProjects {
          nodes {
            id
          }
        }
      }
    }
  `;
  const response = await client.query({
    query: GET_USER,
    context: { headers: { authorization: `Bearer ${token}`, 'DataOps-Operation-Name': 'getUser' } },
  });

  const currentUser = response.data.currentUser;

  const GET_SYSTEM_MANAGEMENT_MEMBERS = gql`
    query getSystemManagementMembers {
      group(fullPath: "snowflake/system-management") {
        id
        fullPath
        groupMembers {
          nodes {
            user {
              id
              username
            }
            accessLevel {
              stringValue
              integerValue
            }
          }
        }
      }
    }
  `;

  try {
    const systemManagementMembersResponse = await client.query({
      query: GET_SYSTEM_MANAGEMENT_MEMBERS,
      context: {
        headers: { authorization: `Bearer ${token}`, 'DataOps-Operation-Name': 'getSystemManagementMembers' },
      },
    });

    currentUser.canPublish = false;

    const systemManagementGroup = systemManagementMembersResponse.data.group;
    if (systemManagementGroup?.groupMembers != null) {
      for (const member of systemManagementGroup.groupMembers.nodes) {
        if (currentUser !== undefined && member.user !== null && currentUser.username === member.user.username) {
          currentUser.canPublish = member.accessLevel.integerValue >= 30;
          break;
        }
      }
    }
  } catch (error) {
    console.error('getSystemManagementMembers error', error);
  }

  const GET_INSTANCE_MEMBERS = gql`
    query getInstancesMembers {
      group(fullPath: "snowflake/instances/${currentUser.username}") {
        groupMembers {
          nodes {
            accessLevel {
              integerValue
            }
          }
        }
      }
    }
  `;

  try {
    const instanceMembersResponse = await client.query({
      query: GET_INSTANCE_MEMBERS,
      context: {
        headers: { authorization: `Bearer ${token}`, 'DataOps-Operation-Name': 'getInstancesMembers' },
      },
    });

    currentUser.canConsume = false;

    const instanceGroup = instanceMembersResponse.data.group;
    if (instanceGroup?.groupMembers?.nodes.length > 0) {
      currentUser.canConsume = true;
    }
  } catch (error) {
    console.error('getInstancesMembers error', error);
  }

  return currentUser;
});

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setBroadcastAcknowledged: (state, action) => {
      state.broadcastIdsAcknowledged = action.payload;
    },
    setBroadcastReset: (state) => {
      state.broadcastIdsAcknowledged = [];
    },
    addTab: (state, action) => {
      // Skip if tab already exists with the same href
      if (state.tabs.some((tab) => tab.href === action.payload.href)) {
        return;
      }

      // Prevent tabs with empty labels from being added
      if (!action.payload.label || action.payload.label.trim() === '') {
        console.warn('Attempted to add tab with empty label:', action.payload);
        return;
      }

      state.tabs.push(action.payload);
    },
    removeTab: (state, action) => {
      state.tabs = state.tabs.filter((t) => t.href !== action.payload.href);
    },
    setTokenRefreshRequired: (state, action) => {
      state.tokenRefreshRequired = action.payload;
    },
    setTokenRefreshAuto: (state, action) => {
      state.tokenRefreshAuto = action.payload;
    },
    setStarProject: (state, action) => {
      if (state.currentUser !== undefined) {
        state.currentUser.starredProjects.nodes.push({ id: action.payload });
      }
    },
    setUnStarProject: (state, action) => {
      if (state.currentUser !== undefined) {
        state.currentUser.starredProjects.nodes = state.currentUser.starredProjects.nodes.filter(
          (project) => project.id !== action.payload,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.requestStatus = 'loading';
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.requestStatus = 'idle';
        state.currentUser = action.payload;
      })
      .addCase(fetchUser.rejected, (state) => {
        state.requestStatus = 'failed';
      });
    builder
      .addCase(fetchUserStars.pending, (state) => {
        state.requestStatus = 'loading';
      })
      .addCase(fetchUserStars.fulfilled, (state, action) => {
        state.requestStatus = 'idle';
        if (state.currentUser !== undefined && action.payload.starredProjects !== undefined) {
          state.currentUser.starredProjects.nodes = action.payload.starredProjects.nodes;
        }
      })
      .addCase(fetchUserStars.rejected, (state) => {
        state.requestStatus = 'failed';
      });
    builder
      .addCase(fetchDocsPipelines.pending, (state) => {
        state.requestStatus = 'loading';
      })
      .addCase(fetchDocsPipelines.fulfilled, (state, action) => {
        state.requestStatus = 'idle';
        state.docsPipelines = action.payload;
      })
      .addCase(fetchDocsPipelines.rejected, (state) => {
        state.requestStatus = 'failed';
      });
  },
});

export const {
  setBroadcastAcknowledged,
  setBroadcastReset,
  addTab,
  removeTab,
  setTokenRefreshRequired,
  setTokenRefreshAuto,
  setStarProject,
  setUnStarProject,
} = accountSlice.actions;

export const selectAcknowledgedBroadcastIds = (state: { account: IAccountState }) =>
  state.account.broadcastIdsAcknowledged;
export const selectTabs = (state: { account: IAccountState }) => state.account.tabs;
export const selectUser = (state: { account: IAccountState }) => state.account.currentUser;
export const selectDocsPipelines = (state: { account: IAccountState }) => state.account.docsPipelines;
export const selectTokenRefreshRequired = (state: { account: IAccountState }) => state.account.tokenRefreshRequired;
export const selectTokenRefreshAuto = (state: { account: IAccountState }) => state.account.tokenRefreshAuto;

export default accountSlice.reducer;
